import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { clientMasterApi } from '@/services/api';
import { ClientFeeNote } from '@/types/client';
import { formatDate } from '@/utils/formatters';
import Swal from 'sweetalert2';
import { Button, Note, Placeholder, confirmColors } from '@/components/ui';

interface FeeNotesProps {
  client_id?: number;
}

const FeeNotes: React.FC<FeeNotesProps> = ({ client_id }) => {
  const [notes, setNotes] = useState<ClientFeeNote[]>([]);
  // Starts true when the mount effect will fetch, so "No fee notes yet" never flashes first.
  const [loading, setLoading] = useState(() => !!client_id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteError, setNoteError] = useState('');

  // Edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchNotes = useCallback(async (cancelled: { current: boolean }) => {
    if (!client_id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await clientMasterApi.getFeeNotes(client_id);
      if (!cancelled.current) setNotes(data);
    } catch {
      if (!cancelled.current) setError('Failed to load fee notes.');
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, [client_id]);

  useEffect(() => {
    const cancelled = { current: false };
    fetchNotes(cancelled);
    return () => { cancelled.current = true; };
  }, [fetchNotes]);

  const handleAdd = async () => {
    if (!noteText.trim()) {
      setNoteError('Note cannot be empty.');
      return;
    }
    if (!client_id) return;
    setSaving(true);
    setNoteError('');
    try {
      await clientMasterApi.addFeeNote(client_id, noteText.trim());
      setNoteText('');
      setShowForm(false);
      await fetchNotes({ current: false });
    } catch {
      setNoteError('Failed to save note. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setNoteText('');
    setNoteError('');
  };

  const handleEditStart = (note: ClientFeeNote) => {
    setEditingId(note.id);
    setEditText(note.note);
    setEditError('');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditText('');
    setEditError('');
  };

  const handleEditSave = async (noteId: number) => {
    if (!editText.trim()) {
      setEditError('Note cannot be empty.');
      return;
    }
    if (!client_id) return;
    setEditSaving(true);
    setEditError('');
    try {
      await clientMasterApi.updateFeeNote(client_id, noteId, editText.trim());
      setEditingId(null);
      setEditText('');
      await fetchNotes({ current: false });
    } catch {
      setEditError('Failed to update note. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (noteId: number) => {
    if (!client_id) return;

    const result = await Swal.fire({
      title: 'Delete Note',
      text: 'Are you sure you want to delete this note? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      ...confirmColors('danger'),
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    setDeletingId(noteId);
    try {
      await clientMasterApi.deleteFeeNote(client_id, noteId);
      await fetchNotes({ current: false });
    } catch {
      Swal.fire('Error', 'Failed to delete note. Please try again.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight={700} sx={{ color: 'var(--text)' }}>
          Fee Notes
        </Typography>
        {client_id && !showForm && (
          <Button
            icon={<AddIcon fontSize="small" />}
            variant="primary"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            Add Note
          </Button>
        )}
      </Stack>

      {error && <Box sx={{ mb: 2 }}><Note tone="error">{error}</Note></Box>}

      {showForm && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid var(--border)', borderRadius: 2, bgcolor: 'var(--surface-2)' }}>
          <TextField
            label="Note"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={noteText}
            onChange={(e) => { setNoteText(e.target.value); setNoteError(''); }}
            placeholder="Enter fee-related note..."
            error={!!noteError}
            helperText={noteError}
            autoFocus
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1.5}>
            <Button size="sm" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleAdd}
              disabled={saving}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : 'Save Note'}
            </Button>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Placeholder kind="loading" />
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 2, maxHeight: 320, overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 140, bgcolor: 'var(--surface-2)' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: 'var(--surface-2)' }}>Note</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 96, bgcolor: 'var(--surface-2)' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No fee notes yet.
                  </TableCell>
                </TableRow>
              ) : (
                notes.map((note) => (
                  <TableRow key={note.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', pt: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>{formatDate(note.created_ts)}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', verticalAlign: 'top' }}>
                      {editingId === note.id ? (
                        <Box>
                          <TextField
                            multiline
                            rows={3}
                            fullWidth
                            size="small"
                            value={editText}
                            onChange={(e) => { setEditText(e.target.value); setEditError(''); }}
                            error={!!editError}
                            helperText={editError}
                            autoFocus
                            disabled={editSaving}
                          />
                          <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                            <Button size="sm" onClick={handleEditCancel} disabled={editSaving}>
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleEditSave(note.id)}
                              disabled={editSaving}
                            >
                              {editSaving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        note.note
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', pt: 1 }}>
                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit">
                              <span>
                                <Button
                                  iconOnly
                                  aria-label="Edit"
                                  onClick={() => handleEditStart(note)}
                                  disabled={deletingId === note.id || editingId !== null}
                                >
                                  <EditIcon fontSize="small" />
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <span>
                                <Button
                                  iconOnly
                                  aria-label="Delete"
                                  style={{ color: 'var(--neg)' }}
                                  onClick={() => handleDelete(note.id)}
                                  disabled={deletingId === note.id || editingId !== null}
                                >
                                  {deletingId === note.id
                                    ? <CircularProgress size={16} color="inherit" />
                                    : <DeleteIcon fontSize="small" />}
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                      </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default FeeNotes;
